import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { AlertTriangle } from "lucide-react";
import { GATE_EVENT } from "../lib/api.js";
import { useOnboarding, ChecklistRows, COMPANY_DETAILS_PATH, BILLING_PATH } from "../lib/onboarding.jsx";
import BillingGateModal from "./BillingGateModal.jsx";

const TITLE = "Finish setting up your account to deploy";

// Where the single call-to-action should go: company details while anything there is
// outstanding, otherwise billing.
function nextStep(items) {
  const todo = items.filter((i) => !i.done);
  const billingOnly = todo.length > 0 && todo.every((i) => i.key === "billing");
  return billingOnly
    ? { to: BILLING_PATH, label: "Set up billing" }
    : { to: COMPANY_DETAILS_PATH, label: "Complete company details" };
}

// Persistent and deliberately not dismissable: while this shows, every deploy is refused.
// Renders nothing for operators on their own account, while loading, and when the
// onboarding state could not be read.
export function OnboardingBanner() {
  const { incomplete, items, supportOrg, graceUntil } = useOnboarding();
  if (!incomplete) return null;
  const step = nextStep(items);
  const by = graceUntil ? new Date(graceUntil).toLocaleDateString(undefined, { day: "numeric", month: "long" }) : null;
  return (
    <div
      role="status"
      style={{
        display: "flex", alignItems: "center", flexWrap: "wrap", gap: "8px 14px",
        padding: "9px 16px", background: "var(--warn-soft)", borderBottom: "1px solid var(--border)",
        flexShrink: 0,
      }}
    >
      <span style={{ display: "inline-flex", alignItems: "center", gap: 8, fontSize: 13, fontWeight: 600, color: "var(--text)", minWidth: 0 }}>
        <AlertTriangle size={15} style={{ color: "var(--warn)", flexShrink: 0 }} aria-hidden="true" />
        {supportOrg
          ? `${supportOrg.name} hasn't finished account setup — their deploys ${by ? `will be blocked after ${by}` : "are blocked"}`
          : by ? `Finish setting up your account by ${by} to keep deploying` : TITLE}
      </span>
      <ChecklistRows items={items} compact />
      <span style={{ flex: 1 }} />
      {!supportOrg && (
        <Link className="btn btn-primary" to={step.to} style={{ padding: "5px 12px", whiteSpace: "nowrap" }}>{step.label}</Link>
      )}
    </div>
  );
}

// Stands in for a create form's submit action while the account is incomplete.
export function OnboardingBlock({ className = "" }) {
  const { blocked, items, supportOrg } = useOnboarding();
  if (!blocked) return null;
  const step = nextStep(items);
  return (
    <div
      className={className}
      role="status"
      style={{ border: "1px solid var(--warn)", background: "var(--warn-soft)", borderRadius: 10, padding: 16 }}
    >
      <div style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 14, fontWeight: 600, color: "var(--text)" }}>
        <AlertTriangle size={16} style={{ color: "var(--warn)", flexShrink: 0 }} aria-hidden="true" />
        {supportOrg ? `${supportOrg.name} hasn't finished account setup` : TITLE}
      </div>
      <p style={{ margin: "6px 0 12px", fontSize: 13, color: "var(--text-muted)", lineHeight: 1.5 }}>
        {supportOrg
          ? "Nothing can be created or deployed for this client until these are done."
          : "Nothing can be created or deployed until these are done. It only takes a couple of minutes."}
      </p>
      <ChecklistRows items={items} />
      <Link className="btn btn-primary" to={step.to} style={{ marginTop: 14 }}>{step.label}</Link>
    </div>
  );
}

// One modal for the whole app. lib/api.js announces every refused action (403
// onboarding_required, 402 insufficient_credit / billing_setup_required / account_suspended)
// on a window event; this turns it into "here is what is missing and where to fix it".
export function GateHost() {
  const { items, status } = useOnboarding();
  const [gate, setGate] = useState(null);

  useEffect(() => {
    const onGate = (e) => { if (e.detail?.code) setGate(e.detail); };
    window.addEventListener(GATE_EVENT, onGate);
    return () => window.removeEventListener(GATE_EVENT, onGate);
  }, []);

  if (!gate) return null;
  // Prefer the live checklist (it includes what is already done); the refusal's own fields
  // are the fallback when the onboarding state is unavailable.
  const rows = gate.code === "onboarding_required" && status === "ready" && items.some((i) => !i.done) ? items : undefined;
  return <BillingGateModal gate={gate} items={rows} onClose={() => setGate(null)} />;
}
